import React, { useEffect, useRef, useState } from 'react'
import api from '../api'
import { GitBranch, RefreshCw, UploadCloud, CheckCircle2, AlertTriangle, Clock3, ChevronDown } from 'lucide-react'
import { Card, PageHeader, Button, Badge, Banner, Alert, EmptyState } from '../components/ui'

// Halaman "Log Fix" (2026-07-25) -- cek pembaruan repo GitHub + riwayat penerapannya. Route ini
// TIDAK menjalankan git/docker sendiri (lihat catatan di updateSystem.js) -- worker host terpisah
// (update_worker.py, cron tiap 2 menit) yg benar2 fetch/pull/deploy, halaman ini cuma baca hasilnya
// dari DB & bisa menulis permintaan "tolong update" lewat flag. Makanya di-poll otomatis saat ada
// proses berjalan (pending/running), supaya progresnya kelihatan tanpa refresh manual.
function relTime(iso) {
  if (!iso) return '-'
  const diffMs = Date.now() - new Date(iso).getTime()
  const min = Math.round(diffMs / 60000)
  if (min < 1) return 'baru saja'
  if (min < 60) return `${min} menit lalu`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr} jam lalu`
  return `${Math.round(hr / 24)} hari lalu`
}

function fmtDate(iso) {
  if (!iso) return '-'
  return new Date(iso).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' })
}

const statusBadge = {
  pending: <Badge tone="neutral">Menunggu worker</Badge>,
  running: <Badge tone="brand">Sedang berjalan</Badge>,
  success: <Badge tone="success">Berhasil</Badge>,
  failed: <Badge tone="danger">Gagal</Badge>,
}

function CommitRow({ c }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
      <code style={{ fontSize: 11.5, color: 'var(--text-faint)', flexShrink: 0 }}>{c.short}</code>
      <span style={{ flex: 1, color: 'var(--text)' }}>{c.message}</span>
      <span style={{ fontSize: 11.5, color: 'var(--text-faint)', flexShrink: 0 }}>{c.author}</span>
    </div>
  )
}

function HistoryItem({ h }) {
  const [open, setOpen] = useState(false)
  const commits = h.commit_list || []
  return (
    <Card style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            {statusBadge[h.status] || <Badge>{h.status}</Badge>}
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{fmtDate(h.requested_at)}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-faint)' }}>
            Diminta oleh <strong>{h.requested_by || '-'}</strong>
            {h.to_commit && <> &middot; <code style={{ fontSize: 11 }}>{h.from_commit?.slice(0, 7)} &rarr; {h.to_commit.slice(0, 7)}</code></>}
          </div>
        </div>
        {h.output_log && (
          <Button size="sm" variant="ghost" icon={ChevronDown} onClick={() => setOpen(!open)}>
            {open ? 'Sembunyikan log' : 'Lihat log'}
          </Button>
        )}
      </div>

      {commits.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {commits.map(c => <CommitRow key={c.hash} c={c} />)}
        </div>
      )}

      {open && h.output_log && (
        <pre style={{
          marginTop: 12, background: '#101018', color: '#d7d7e0', padding: 14, borderRadius: 8,
          fontSize: 11.5, lineHeight: 1.6, overflowX: 'auto', maxHeight: 360, overflowY: 'auto'
        }}>{h.output_log}</pre>
      )}
    </Card>
  )
}

export default function LogFix() {
  const [status, setStatus] = useState(null)
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [applying, setApplying] = useState(false)
  const [pesan, setPesan] = useState(null)
  const pollRef = useRef(null)

  async function load() {
    const [s, h] = await Promise.all([api.get('/update/status'), api.get('/update/history')])
    setStatus(s.data)
    setHistory(h.data)
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  // Poll tiap 5 detik SELAMA ada proses pending/running -- berhenti otomatis begitu selesai
  // supaya tidak nge-poll selamanya kalau halaman dibiarkan terbuka lama dlm keadaan idle.
  useEffect(() => {
    const sedangProses = !!status?.current
    if (sedangProses && !pollRef.current) {
      pollRef.current = setInterval(load, 5000)
    } else if (!sedangProses && pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    return () => { if (!sedangProses && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null } }
  }, [status?.current])

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current) }, [])

  async function terapkanUpdate() {
    const n = status?.pendingCommits?.length || 0
    if (!window.confirm(
      `Terapkan ${n} pembaruan sekarang?\n\nadmin-app, admin-bot, dan admin-radius akan di-restart satu per satu (WA chatbot sempat putus beberapa detik, pesan yang masuk saat itu tetap tersimpan & terkirim setelah reconnect). Proses berjalan di background, mulai dalam 1-2 menit.`
    )) return
    setApplying(true); setPesan(null)
    try {
      const res = await api.post('/update/apply')
      setPesan({ type: 'success', text: res.data.message })
      load()
    } catch (e) {
      setPesan({ type: 'error', text: e.response?.data?.message || 'Gagal meminta update' })
    } finally { setApplying(false) }
  }

  if (loading) return null

  const sedangProses = !!status?.current
  const adaPending = (status?.pendingCommits?.length || 0) > 0

  return (
    <div>
      <PageHeader
        title="Log Fix"
        subtitle="Cek pembaruan dari GitHub & riwayat penerapannya"
        actions={<Button variant="ghost" icon={RefreshCw} onClick={load}>Muat Ulang</Button>}
      />

      {pesan && <Alert type={pesan.type}>{pesan.text}</Alert>}

      {status?.checkError && (
        <Alert type="error">Gagal cek pembaruan dari GitHub: {status.checkError}</Alert>
      )}

      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
              <GitBranch size={16} style={{ color: 'var(--text-muted)' }} />
              <span style={{ fontWeight: 700, fontSize: 14.5 }}>
                {sedangProses
                  ? 'Pembaruan sedang diproses...'
                  : adaPending
                    ? `${status.pendingCommits.length} pembaruan tersedia`
                    : 'Sudah versi terbaru'}
              </span>
              {sedangProses
                ? <Badge tone="brand">{status.current.status === 'running' ? 'Berjalan' : 'Menunggu worker'}</Badge>
                : adaPending
                  ? <Badge tone="warning">Update tersedia</Badge>
                  : <Badge tone="success"><CheckCircle2 size={12} /> Terbaru</Badge>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-faint)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <code>{status?.localCommit?.slice(0, 7) || '-'}</code> berjalan saat ini
              <Clock3 size={11} /> dicek {relTime(status?.lastCheckedAt)}
            </div>
          </div>
          <Button icon={UploadCloud} loading={applying}
            disabled={sedangProses || !adaPending}
            onClick={terapkanUpdate}>
            {sedangProses ? 'Sedang memperbarui...' : 'Update Sekarang'}
          </Button>
        </div>

        {adaPending && !sedangProses && (
          <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            {status.pendingCommits.map(c => <CommitRow key={c.hash} c={c} />)}
          </div>
        )}
      </Card>

      {sedangProses && (
        <Banner tone="brand">
          <RefreshCw size={14} style={{ flexShrink: 0 }} className="spin" />
          Worker host akan mengambil permintaan ini dalam 1-2 menit (cron tiap 2 menit), lalu menjalankan git pull &amp; deploy.sh. Halaman ini refresh otomatis.
        </Banner>
      )}

      <h3 style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-muted)', margin: '22px 0 10px', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        Riwayat Update
      </h3>
      {history.length === 0
        ? <EmptyState icon={AlertTriangle} title="Belum ada riwayat update" hint="Riwayat akan muncul di sini setelah update pertama diterapkan" />
        : history.map(h => <HistoryItem key={h.id} h={h} />)}
    </div>
  )
}
